/**
 * PropertiesPanel — Advanced Element Style Editor
 *
 * Professional-grade property inspector with:
 * - Typography controls (font, size, weight, color, line-height, letter-spacing, decoration, transform)
 * - Layout controls (display, flex, grid, gap, align, justify)
 * - Size controls (width, height, min/max)
 * - Background (color, opacity, gradient)
 * - Spacing (visual box model editor)
 * - Border (width, color, radius, style)
 * - Box Shadow editor (multiple shadows)
 * - Text Shadow editor
 * - Transform controls (rotate, scale, skew, translate)
 * - Filter effects (blur, brightness, contrast, grayscale, saturate, sepia, hue-rotate)
 * - Transition editor
 * - Overflow controls
 * - Position controls
 * - Cursor selector
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronDown, X,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Underline, Strikethrough,
  Type, Palette, Box, Layers, Move, Maximize,
  Image as ImageIcon, Link2, RotateCw, Sliders,
  Droplets, SunDim, MousePointer, ArrowUpDown
} from 'lucide-react';
import ColorPicker from './ColorPicker';
import SpacingEditor from './SpacingEditor';

// ─── Element type sets ────────────────────────────────────────────────
const TEXT_TAGS = new Set(['h1','h2','h3','h4','h5','h6','p','span','a','button','li','label']);
const CONTAINER_TAGS = new Set(['div','section','header','footer','main','nav','article']);
const IMAGE_TAGS = new Set(['img']);

// ─── Option lists ─────────────────────────────────────────────────────
const FONT_FAMILIES = [
  'inherit','Inter','DM Sans','Poppins','Montserrat',
  'Playfair Display','Cormorant Garamond','Lora',
  'Georgia','Libre Baskerville','Source Serif Pro',
  'Roboto','Lato','Open Sans','Raleway',
  'Josefin Sans','Crimson Text','Merriweather',
  'Arial','Helvetica','Times New Roman',
];
const FONT_WEIGHTS = [100,200,300,400,500,600,700,800,900];
const BORDER_STYLES = ['none','solid','dashed','dotted','double','groove','ridge'];
const OBJECT_FIT_OPTIONS = ['cover','contain','fill','none','scale-down'];
const DISPLAY_OPTIONS = ['block','flex','grid','inline','inline-block','inline-flex','none'];
const FLEX_DIRECTION_OPTIONS = ['row','column','row-reverse','column-reverse'];
const FLEX_WRAP_OPTIONS = ['nowrap','wrap','wrap-reverse'];
const JUSTIFY_CONTENT_OPTIONS = ['flex-start','center','flex-end','space-between','space-around','space-evenly'];
const ALIGN_ITEMS_OPTIONS = ['stretch','flex-start','center','flex-end','baseline'];
const POSITION_OPTIONS = ['static','relative','absolute','fixed','sticky'];
const OVERFLOW_OPTIONS = ['visible','hidden','scroll','auto'];
const CURSOR_OPTIONS = ['auto','default','pointer','move','text','not-allowed','crosshair','grab','zoom-in','zoom-out'];
const TEXT_TRANSFORM_OPTIONS = ['none','uppercase','lowercase','capitalize'];
const BLEND_MODES = ['normal','multiply','screen','overlay','darken','lighten','color-dodge','color-burn','soft-light'];
const TRANSITION_PROPERTIES = ['all','opacity','transform','background','color','border','box-shadow','filter'];
const TRANSITION_DURATIONS = ['0.15s','0.2s','0.3s','0.5s','0.8s','1s'];
const TRANSITION_EASINGS = ['ease','ease-in','ease-out','ease-in-out','linear','cubic-bezier(0.4, 0, 0.2, 1)'];

// ─── Shared styles ────────────────────────────────────────────────────
const st = {
  panel: {
    width: 320, background: '#ffffff', borderLeft: '1px solid #e5e7eb',
    height: '100%', display: 'flex', flexDirection: 'column',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: '#f8f9fa',
    position: 'sticky', top: 0, zIndex: 10,
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  headerTitle: { display: 'flex', alignItems: 'center', gap: 8 },
  badge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '2px 8px', borderRadius: 4, background: '#eef2ff', color: '#4f46e5',
    fontSize: 11, fontWeight: 700, fontFamily: "'SF Mono','Fira Code','Consolas',monospace",
    textTransform: 'uppercase', letterSpacing: '0.03em',
  },
  headerLabel: { fontSize: 13, fontWeight: 600, color: '#374151' },
  nodePath: {
    fontSize: 10, color: '#9ca3af',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220,
  },
  closeBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, border: 'none', borderRadius: 6,
    background: 'transparent', color: '#9ca3af', cursor: 'pointer',
    padding: 0, flexShrink: 0, transition: 'all 0.15s ease',
  },
  scrollArea: { flex: 1, overflowY: 'auto' },
  controlRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  controlLabel: { fontSize: 12, color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap' },
  input: {
    height: 32, border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '0 10px', fontSize: 12, color: '#374151', background: '#ffffff',
    outline: 'none', transition: 'border-color 0.15s ease', fontFamily: "'Inter',sans-serif",
  },
  select: {
    height: 32, border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '0 8px', fontSize: 12, color: '#374151', background: '#ffffff',
    outline: 'none', cursor: 'pointer', transition: 'border-color 0.15s ease',
    fontFamily: "'Inter',sans-serif", appearance: 'auto',
  },
  toggleGroup: { display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 8, padding: 2 },
  toggleBtn: (isActive) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 28, border: 'none', borderRadius: 6,
    background: isActive ? '#ffffff' : 'transparent',
    color: isActive ? '#374151' : '#94a3b8', cursor: 'pointer', padding: 0,
    transition: 'all 0.15s ease',
    boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
  }),
  rangeRow: { display: 'flex', alignItems: 'center', gap: 8 },
  rangeInput: { flex: 1, height: 4, appearance: 'auto', cursor: 'pointer', accentColor: '#4f46e5' },
  rangeValue: { fontSize: 12, color: '#374151', fontWeight: 500, minWidth: 32, textAlign: 'right' },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    border: 'none', background: '#f8f9fa', padding: '10px 16px',
    cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151',
    textAlign: 'left', transition: 'background 0.15s ease',
  },
  sectionContent: { padding: '12px 16px' },
  row: { display: 'flex', gap: 8, marginBottom: 8 },
  halfCol: { flex: 1 },
};

// ─── Collapsible Section ──────────────────────────────────────────────
function CollapsibleSection({ title, icon: Icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <button
        onClick={() => setOpen(!open)}
        style={st.sectionHeader}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
      >
        <ChevronDown size={14} style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease', color: '#9ca3af', flexShrink: 0 }} />
        {Icon && <Icon size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />}
        <span style={{ flex: 1 }}>{title}</span>
      </button>
      {open && <div style={st.sectionContent}>{children}</div>}
    </div>
  );
}

// ─── Style Input ──────────────────────────────────────────────────────
function StyleInput({ label, value, onChange, suffix = 'px', type = 'number', placeholder, width }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={st.controlRow}>
      <span style={st.controlLabel}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || ''}
          style={{ ...st.input, width: width || (type === 'text' ? 100 : 64), borderColor: focused ? '#818cf8' : '#e5e7eb' }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        {suffix && <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{suffix}</span>}
      </div>
    </div>
  );
}

// ─── Style Select ─────────────────────────────────────────────────────
function StyleSelect({ label, value, onChange, options, width }) {
  const [focused, setFocused] = useState(false);
  const normalizedOptions = options.map((opt) => typeof opt === 'object' ? opt : { value: opt, label: opt });
  return (
    <div style={st.controlRow}>
      <span style={st.controlLabel}>{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...st.select, width: width || 140, borderColor: focused ? '#818cf8' : '#e5e7eb' }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <option value="">—</option>
        {normalizedOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}

// ─── Range Slider ─────────────────────────────────────────────────────
function RangeControl({ label, value, onChange, min = 0, max = 100, step = 1, suffix = '', format }) {
  const numVal = parseFloat(value) || 0;
  return (
    <div style={st.controlRow}>
      <span style={st.controlLabel}>{label}</span>
      <div style={st.rangeRow}>
        <input type="range" min={min} max={max} step={step} value={numVal}
          onChange={(e) => onChange(parseFloat(e.target.value))} style={st.rangeInput} />
        <span style={st.rangeValue}>{format ? format(numVal) : `${numVal}${suffix}`}</span>
      </div>
    </div>
  );
}

// ─── Shadow Editor Row ────────────────────────────────────────────────
function ShadowEditor({ label, shadow, onChange }) {
  const [expanded, setExpanded] = useState(false);
  // Parse shadow: "Xpx Ypx Bpx Spx color"
  const parts = (shadow || '').match(/([-\d.]+)px\s+([-\d.]+)px\s+([-\d.]+)px\s*([-\d.]+)px?\s*(.*)/);
  const x = parts ? parseFloat(parts[1]) : 0;
  const y = parts ? parseFloat(parts[2]) : 4;
  const blur = parts ? parseFloat(parts[3]) : 12;
  const spread = parts ? parseFloat(parts[4]) : 0;
  const color = parts ? parts[5].trim() || 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.1)';

  const update = (field, val) => {
    const newShadow = {
      x: field === 'x' ? val : x,
      y: field === 'y' ? val : y,
      blur: field === 'blur' ? val : blur,
      spread: field === 'spread' ? val : spread,
      color: field === 'color' ? val : color,
    };
    onChange(`${newShadow.x}px ${newShadow.y}px ${newShadow.blur}px ${newShadow.spread}px ${newShadow.color}`);
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ ...st.controlRow, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <span style={st.controlLabel}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: 24, height: 16, borderRadius: 4,
            background: '#fff', border: '1px solid #e5e7eb',
            boxShadow: shadow || 'none',
          }} />
          <ChevronDown size={12} style={{ color: '#9ca3af', transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
        </div>
      </div>
      {expanded && (
        <div style={{ padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
          <div style={st.row}>
            <div style={st.halfCol}>
              <label style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, display: 'block' }}>X Offset</label>
              <input type="number" value={x} onChange={(e) => update('x', parseFloat(e.target.value) || 0)}
                style={{ ...st.input, width: '100%' }} />
            </div>
            <div style={st.halfCol}>
              <label style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, display: 'block' }}>Y Offset</label>
              <input type="number" value={y} onChange={(e) => update('y', parseFloat(e.target.value) || 0)}
                style={{ ...st.input, width: '100%' }} />
            </div>
          </div>
          <div style={st.row}>
            <div style={st.halfCol}>
              <label style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, display: 'block' }}>Blur</label>
              <input type="number" min="0" value={blur} onChange={(e) => update('blur', Math.max(0, parseFloat(e.target.value) || 0))}
                style={{ ...st.input, width: '100%' }} />
            </div>
            <div style={st.halfCol}>
              <label style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, display: 'block' }}>Spread</label>
              <input type="number" value={spread} onChange={(e) => update('spread', parseFloat(e.target.value) || 0)}
                style={{ ...st.input, width: '100%' }} />
            </div>
          </div>
          <label style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, display: 'block' }}>Shadow Color</label>
          <input type="text" value={color} onChange={(e) => update('color', e.target.value)}
            style={{ ...st.input, width: '100%' }} placeholder="rgba(0,0,0,0.1)" />
          {/* Quick presets */}
          <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { label: 'None', val: '' },
              { label: 'Soft', val: '0px 2px 8px 0px rgba(0,0,0,0.08)' },
              { label: 'Medium', val: '0px 4px 16px 0px rgba(0,0,0,0.12)' },
              { label: 'Strong', val: '0px 8px 32px 0px rgba(0,0,0,0.18)' },
              { label: 'Dramatic', val: '0px 20px 60px 0px rgba(0,0,0,0.25)' },
              { label: 'Glow', val: '0px 0px 24px 4px rgba(99,102,241,0.3)' },
            ].map(p => (
              <button key={p.label} onClick={() => onChange(p.val)} style={{
                padding: '3px 8px', borderRadius: 6,
                border: '1px solid #e5e7eb', background: '#fff', fontSize: 10,
                color: '#6b7280', cursor: 'pointer', transition: 'all 0.15s',
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#818cf8'; e.currentTarget.style.color = '#4f46e5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────
function parseNumeric(val) {
  if (val === null || val === undefined || val === '') return '';
  const num = parseFloat(String(val));
  return isNaN(num) ? '' : num;
}

function parseSpacing(style, prefix) {
  return {
    top: style?.[`${prefix}Top`] || style?.[`${prefix}-top`] || 0,
    right: style?.[`${prefix}Right`] || style?.[`${prefix}-right`] || 0,
    bottom: style?.[`${prefix}Bottom`] || style?.[`${prefix}-bottom`] || 0,
    left: style?.[`${prefix}Left`] || style?.[`${prefix}-left`] || 0,
  };
}

// ─── Human-readable element description ──────────────────────────────
function getElementDescription(tagName, type) {
  const TAG_LABELS = {
    h1: 'Main Heading', h2: 'Section Heading', h3: 'Sub-heading', h4: 'Small Heading',
    h5: 'Minor Heading', h6: 'Tiny Heading', p: 'Paragraph', span: 'Text',
    a: 'Link', button: 'Button', li: 'List Item', label: 'Label',
    div: 'Container', section: 'Section', header: 'Header Area', footer: 'Footer Area',
    main: 'Main Content', nav: 'Navigation', article: 'Article',
    img: 'Image',
  };
  if (type === 'text') return TAG_LABELS[tagName] || 'Text Element';
  if (type === 'image') return 'Image';
  return TAG_LABELS[tagName] || 'Element';
}

// ─── Main Component ──────────────────────────────────────────────────
const PropertiesPanel = ({ selectedElement, themeColors, onChange, onClose }) => {
  const nodePath = selectedElement?.nodePath;
  const structure = selectedElement?.structure;
  const type = selectedElement?.type;

  const handleChange = useCallback((property, value, changeType) => {
    if (nodePath) onChange(nodePath, property, value, changeType);
  }, [onChange, nodePath]);

  const handlePxChange = useCallback((property) => (value) => {
    const trimmed = String(value).trim();
    if (trimmed === '') handleChange(property, '');
    else { const num = parseFloat(trimmed); if (!isNaN(num)) handleChange(property, `${num}px`); }
  }, [handleChange]);

  const handleDirectChange = useCallback((property) => (value) => {
    handleChange(property, value);
  }, [handleChange]);

  const handlePaddingChange = useCallback((side, value) => {
    handleChange(`padding${side.charAt(0).toUpperCase() + side.slice(1)}`, value);
  }, [handleChange]);

  const handleMarginChange = useCallback((side, value) => {
    handleChange(`margin${side.charAt(0).toUpperCase() + side.slice(1)}`, value);
  }, [handleChange]);

  if (!selectedElement) return null;

  const elementStyle = structure?.style || {};
  const tagName = (selectedElement?.tagName || structure?.type || 'div').toLowerCase();
  const isText = TEXT_TAGS.has(tagName) || type === 'text';
  const isContainer = CONTAINER_TAGS.has(tagName);
  const isImage = IMAGE_TAGS.has(tagName) || type === 'image';
  const displayValue = elementStyle.display || '';
  const isFlex = displayValue === 'flex' || displayValue === 'inline-flex';

  return (
    <div style={st.panel}>
      {/* ── Header ── */}
      <div style={st.header}>
        <div style={st.headerLeft}>
          <div style={st.headerTitle}>
            <span style={st.badge}>{tagName}</span>
            <span style={st.headerLabel}>{getElementDescription(tagName, type)}</span>
          </div>
          <span style={st.nodePath}>Edit the styling of this element</span>
        </div>
        <button onClick={onClose} style={st.closeBtn}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#374151'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af'; }}
        >
          <X size={16} />
        </button>
      </div>

      {/* ── Scrollable content ── */}
      <div style={st.scrollArea}>

        {/* ── Typography ── */}
        {isText && (
          <CollapsibleSection title="Typography" icon={Type} defaultOpen>
            <StyleSelect label="Font Family" value={elementStyle.fontFamily} onChange={handleDirectChange('fontFamily')} options={FONT_FAMILIES} />
            <StyleInput label="Font Size" value={parseNumeric(elementStyle.fontSize)} onChange={handlePxChange('fontSize')} suffix="px" />
            <StyleSelect label="Font Weight" value={elementStyle.fontWeight}
              onChange={handleDirectChange('fontWeight')}
              options={FONT_WEIGHTS.map((w) => ({ value: String(w), label: String(w) }))} />

            <div style={{ marginBottom: 8 }}>
              <span style={{ ...st.controlLabel, display: 'block', marginBottom: 6 }}>Text Color</span>
              <ColorPicker value={elementStyle.color || ''} onChange={(c) => handleChange('color', c)} themeColors={themeColors} label="" />
            </div>

            <StyleInput label="Line Height" value={parseNumeric(elementStyle.lineHeight)} onChange={handleDirectChange('lineHeight')} suffix="" />
            <StyleInput label="Letter Spacing" value={parseNumeric(elementStyle.letterSpacing)} onChange={handlePxChange('letterSpacing')} suffix="px" />

            {/* Text Align */}
            <div style={st.controlRow}>
              <span style={st.controlLabel}>Text Align</span>
              <div style={st.toggleGroup}>
                {[
                  { value: 'left', icon: AlignLeft },
                  { value: 'center', icon: AlignCenter },
                  { value: 'right', icon: AlignRight },
                  { value: 'justify', icon: AlignJustify },
                ].map(({ value, icon: Icon }) => (
                  <button key={value} title={value}
                    onClick={() => handleChange('textAlign', value)}
                    style={st.toggleBtn(elementStyle.textAlign === value)}
                  ><Icon size={14} /></button>
                ))}
              </div>
            </div>

            {/* Text Decoration */}
            <div style={st.controlRow}>
              <span style={st.controlLabel}>Decoration</span>
              <div style={st.toggleGroup}>
                {[
                  { value: 'underline', icon: Underline, title: 'Underline' },
                  { value: 'line-through', icon: Strikethrough, title: 'Strikethrough' },
                  { value: 'none', icon: X, title: 'None' },
                ].map(({ value, icon: Icon, title }) => (
                  <button key={value} title={title}
                    onClick={() => handleChange('textDecoration', value)}
                    style={st.toggleBtn(elementStyle.textDecoration === value)}
                  ><Icon size={14} /></button>
                ))}
              </div>
            </div>

            {/* Text Transform */}
            <StyleSelect label="Transform" value={elementStyle.textTransform} onChange={handleDirectChange('textTransform')} options={TEXT_TRANSFORM_OPTIONS} />
          </CollapsibleSection>
        )}

        {/* ── Text Shadow (text only) ── */}
        {isText && (
          <CollapsibleSection title="Text Shadow" icon={Type}>
            <ShadowEditor
              label="Text Shadow"
              shadow={elementStyle.textShadow}
              onChange={(val) => handleChange('textShadow', val)}
            />
          </CollapsibleSection>
        )}

        {/* ── Link ── */}
        {(tagName === 'a' || isText) && (
          <CollapsibleSection title="Link" icon={Link2} defaultOpen={tagName === 'a'}>
            <StyleInput label="URL (href)"
              value={structure?.attributes?.href || structure?.href || ''}
              onChange={(val) => handleChange('href', val, 'attribute')}
              suffix="" type="text" placeholder="https://..." />
            <StyleSelect label="Target"
              value={structure?.attributes?.target || structure?.target || ''}
              onChange={(val) => handleChange('target', val, 'attribute')}
              options={[{ value: '_self', label: 'Same tab' }, { value: '_blank', label: 'New tab' }]} />
          </CollapsibleSection>
        )}

        {/* ── Image ── */}
        {isImage && (
          <CollapsibleSection title="Image" icon={ImageIcon} defaultOpen>
            <StyleSelect label="Object Fit" value={elementStyle.objectFit} onChange={handleDirectChange('objectFit')} options={OBJECT_FIT_OPTIONS} />
            <StyleInput label="Border Radius" value={parseNumeric(elementStyle.borderRadius)} onChange={handlePxChange('borderRadius')} suffix="px" />
            <RangeControl label="Opacity" value={elementStyle.opacity ?? 1}
              onChange={(v) => handleChange('opacity', v)} min={0} max={1} step={0.05} format={(v) => `${(v * 100).toFixed(0)}%`} />
          </CollapsibleSection>
        )}

        {/* ── Layout ── */}
        {isContainer && (
          <CollapsibleSection title="Layout" icon={Layers} defaultOpen>
            <StyleSelect label="Display" value={elementStyle.display} onChange={handleDirectChange('display')} options={DISPLAY_OPTIONS} />
            {isFlex && (
              <>
                <StyleSelect label="Direction" value={elementStyle.flexDirection} onChange={handleDirectChange('flexDirection')} options={FLEX_DIRECTION_OPTIONS} />
                <StyleSelect label="Wrap" value={elementStyle.flexWrap} onChange={handleDirectChange('flexWrap')} options={FLEX_WRAP_OPTIONS} />
                <StyleSelect label="Justify" value={elementStyle.justifyContent} onChange={handleDirectChange('justifyContent')} options={JUSTIFY_CONTENT_OPTIONS} />
                <StyleSelect label="Align Items" value={elementStyle.alignItems} onChange={handleDirectChange('alignItems')} options={ALIGN_ITEMS_OPTIONS} />
              </>
            )}
            <StyleInput label="Gap" value={parseNumeric(elementStyle.gap)} onChange={handlePxChange('gap')} suffix="px" />
          </CollapsibleSection>
        )}

        {/* ── Size ── */}
        {isContainer && (
          <CollapsibleSection title="Size" icon={Maximize}>
            <StyleInput label="Width" value={elementStyle.width || ''} onChange={handleDirectChange('width')} suffix="" type="text" placeholder="auto" />
            <StyleInput label="Height" value={elementStyle.height || ''} onChange={handleDirectChange('height')} suffix="" type="text" placeholder="auto" />
            <StyleInput label="Max Width" value={elementStyle.maxWidth || ''} onChange={handleDirectChange('maxWidth')} suffix="" type="text" placeholder="none" />
            <StyleInput label="Min Height" value={elementStyle.minHeight || ''} onChange={handleDirectChange('minHeight')} suffix="" type="text" placeholder="auto" />
          </CollapsibleSection>
        )}

        {/* ── Position ── */}
        {isContainer && (
          <CollapsibleSection title="Position" icon={Move}>
            <StyleSelect label="Position" value={elementStyle.position} onChange={handleDirectChange('position')} options={POSITION_OPTIONS} />
            {elementStyle.position && elementStyle.position !== 'static' && (
              <>
                <StyleInput label="Top" value={elementStyle.top || ''} onChange={handleDirectChange('top')} suffix="" type="text" placeholder="auto" />
                <StyleInput label="Right" value={elementStyle.right || ''} onChange={handleDirectChange('right')} suffix="" type="text" placeholder="auto" />
                <StyleInput label="Bottom" value={elementStyle.bottom || ''} onChange={handleDirectChange('bottom')} suffix="" type="text" placeholder="auto" />
                <StyleInput label="Left" value={elementStyle.left || ''} onChange={handleDirectChange('left')} suffix="" type="text" placeholder="auto" />
                <StyleInput label="Z-Index" value={elementStyle.zIndex || ''} onChange={handleDirectChange('zIndex')} suffix="" type="number" placeholder="auto" />
              </>
            )}
          </CollapsibleSection>
        )}

        {/* ── Background ── */}
        <CollapsibleSection title="Background" icon={Palette}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ ...st.controlLabel, display: 'block', marginBottom: 6 }}>Background Color</span>
            <ColorPicker
              value={elementStyle.backgroundColor || elementStyle.background || ''}
              onChange={(c) => handleChange('backgroundColor', c)}
              themeColors={themeColors} label=""
            />
          </div>
          <RangeControl label="Opacity" value={elementStyle.opacity ?? 1}
            onChange={(v) => handleChange('opacity', v)} min={0} max={1} step={0.05} format={(v) => v.toFixed(2)} />
          <StyleSelect label="Blend Mode" value={elementStyle.mixBlendMode} onChange={handleDirectChange('mixBlendMode')} options={BLEND_MODES} />
        </CollapsibleSection>

        {/* ── Spacing ── */}
        <CollapsibleSection title="Spacing" icon={Move}>
          <SpacingEditor
            padding={parseSpacing(elementStyle, 'padding')}
            margin={parseSpacing(elementStyle, 'margin')}
            onPaddingChange={handlePaddingChange}
            onMarginChange={handleMarginChange}
          />
        </CollapsibleSection>

        {/* ── Border ── */}
        <CollapsibleSection title="Border" icon={Box}>
          <StyleInput label="Border Width" value={parseNumeric(elementStyle.borderWidth)} onChange={handlePxChange('borderWidth')} suffix="px" />
          <div style={{ marginBottom: 8 }}>
            <span style={{ ...st.controlLabel, display: 'block', marginBottom: 6 }}>Border Color</span>
            <ColorPicker value={elementStyle.borderColor || ''} onChange={(c) => handleChange('borderColor', c)} themeColors={themeColors} label="" />
          </div>
          <StyleInput label="Border Radius" value={parseNumeric(elementStyle.borderRadius)} onChange={handlePxChange('borderRadius')} suffix="px" />
          <StyleSelect label="Border Style" value={elementStyle.borderStyle} onChange={handleDirectChange('borderStyle')} options={BORDER_STYLES} />
        </CollapsibleSection>

        {/* ── Box Shadow ── */}
        <CollapsibleSection title="Box Shadow" icon={Droplets}>
          <ShadowEditor
            label="Shadow"
            shadow={elementStyle.boxShadow}
            onChange={(val) => handleChange('boxShadow', val)}
          />
        </CollapsibleSection>

        {/* ── Transform ── */}
        <CollapsibleSection title="Transform" icon={RotateCw}>
          <RangeControl label="Rotate" value={parseNumeric(elementStyle._rotate) || 0}
            onChange={(v) => {
              handleChange('_rotate', v);
              const scale = elementStyle._scale || 1;
              handleChange('transform', `rotate(${v}deg) scale(${scale})`);
            }} min={-180} max={180} step={1} suffix="°" />
          <RangeControl label="Scale" value={elementStyle._scale || 1}
            onChange={(v) => {
              handleChange('_scale', v);
              const rotate = elementStyle._rotate || 0;
              handleChange('transform', `rotate(${rotate}deg) scale(${v})`);
            }} min={0.1} max={3} step={0.05} format={(v) => `${(v * 100).toFixed(0)}%`} />
          <StyleInput label="Translate X" value={elementStyle._translateX || ''} onChange={(val) => {
            handleChange('_translateX', val);
            const y = elementStyle._translateY || '0';
            handleChange('transform', `translate(${val || '0'}px, ${y}px)`);
          }} suffix="px" />
          <StyleInput label="Translate Y" value={elementStyle._translateY || ''} onChange={(val) => {
            handleChange('_translateY', val);
            const x = elementStyle._translateX || '0';
            handleChange('transform', `translate(${x}px, ${val || '0'}px)`);
          }} suffix="px" />
        </CollapsibleSection>

        {/* ── Filters ── */}
        <CollapsibleSection title="Filters" icon={SunDim}>
          <RangeControl label="Blur" value={parseNumeric(elementStyle._filterBlur) || 0}
            onChange={(v) => {
              handleChange('_filterBlur', v);
              const b = v, br = elementStyle._filterBrightness ?? 100, c = elementStyle._filterContrast ?? 100;
              const g = elementStyle._filterGrayscale ?? 0, sa = elementStyle._filterSaturate ?? 100, se = elementStyle._filterSepia ?? 0;
              handleChange('filter', `blur(${b}px) brightness(${br}%) contrast(${c}%) grayscale(${g}%) saturate(${sa}%) sepia(${se}%)`);
            }} min={0} max={20} step={0.5} suffix="px" />
          <RangeControl label="Brightness" value={elementStyle._filterBrightness ?? 100}
            onChange={(v) => {
              handleChange('_filterBrightness', v);
              const b = elementStyle._filterBlur ?? 0, c = elementStyle._filterContrast ?? 100;
              const g = elementStyle._filterGrayscale ?? 0, sa = elementStyle._filterSaturate ?? 100, se = elementStyle._filterSepia ?? 0;
              handleChange('filter', `blur(${b}px) brightness(${v}%) contrast(${c}%) grayscale(${g}%) saturate(${sa}%) sepia(${se}%)`);
            }} min={0} max={200} step={5} suffix="%" />
          <RangeControl label="Contrast" value={elementStyle._filterContrast ?? 100}
            onChange={(v) => {
              handleChange('_filterContrast', v);
              const b = elementStyle._filterBlur ?? 0, br = elementStyle._filterBrightness ?? 100;
              const g = elementStyle._filterGrayscale ?? 0, sa = elementStyle._filterSaturate ?? 100, se = elementStyle._filterSepia ?? 0;
              handleChange('filter', `blur(${b}px) brightness(${br}%) contrast(${v}%) grayscale(${g}%) saturate(${sa}%) sepia(${se}%)`);
            }} min={0} max={200} step={5} suffix="%" />
          <RangeControl label="Grayscale" value={elementStyle._filterGrayscale ?? 0}
            onChange={(v) => {
              handleChange('_filterGrayscale', v);
              const b = elementStyle._filterBlur ?? 0, br = elementStyle._filterBrightness ?? 100, c = elementStyle._filterContrast ?? 100;
              const sa = elementStyle._filterSaturate ?? 100, se = elementStyle._filterSepia ?? 0;
              handleChange('filter', `blur(${b}px) brightness(${br}%) contrast(${c}%) grayscale(${v}%) saturate(${sa}%) sepia(${se}%)`);
            }} min={0} max={100} step={5} suffix="%" />
          <RangeControl label="Saturate" value={elementStyle._filterSaturate ?? 100}
            onChange={(v) => {
              handleChange('_filterSaturate', v);
              const b = elementStyle._filterBlur ?? 0, br = elementStyle._filterBrightness ?? 100, c = elementStyle._filterContrast ?? 100;
              const g = elementStyle._filterGrayscale ?? 0, se = elementStyle._filterSepia ?? 0;
              handleChange('filter', `blur(${b}px) brightness(${br}%) contrast(${c}%) grayscale(${g}%) saturate(${v}%) sepia(${se}%)`);
            }} min={0} max={200} step={5} suffix="%" />
          <RangeControl label="Sepia" value={elementStyle._filterSepia ?? 0}
            onChange={(v) => {
              handleChange('_filterSepia', v);
              const b = elementStyle._filterBlur ?? 0, br = elementStyle._filterBrightness ?? 100, c = elementStyle._filterContrast ?? 100;
              const g = elementStyle._filterGrayscale ?? 0, sa = elementStyle._filterSaturate ?? 100;
              handleChange('filter', `blur(${b}px) brightness(${br}%) contrast(${c}%) grayscale(${g}%) saturate(${sa}%) sepia(${v}%)`);
            }} min={0} max={100} step={5} suffix="%" />
        </CollapsibleSection>

        {/* ── Transition ── */}
        <CollapsibleSection title="Transition" icon={Sliders}>
          <StyleSelect label="Property" value={elementStyle.transitionProperty}
            onChange={handleDirectChange('transitionProperty')} options={TRANSITION_PROPERTIES} />
          <StyleSelect label="Duration" value={elementStyle.transitionDuration}
            onChange={handleDirectChange('transitionDuration')} options={TRANSITION_DURATIONS} />
          <StyleSelect label="Easing" value={elementStyle.transitionTimingFunction}
            onChange={handleDirectChange('transitionTimingFunction')} options={TRANSITION_EASINGS} />
        </CollapsibleSection>

        {/* ── Overflow ── */}
        {isContainer && (
          <CollapsibleSection title="Overflow" icon={ArrowUpDown}>
            <StyleSelect label="Overflow X" value={elementStyle.overflowX} onChange={handleDirectChange('overflowX')} options={OVERFLOW_OPTIONS} />
            <StyleSelect label="Overflow Y" value={elementStyle.overflowY} onChange={handleDirectChange('overflowY')} options={OVERFLOW_OPTIONS} />
          </CollapsibleSection>
        )}

        {/* ── Cursor ── */}
        <CollapsibleSection title="Cursor" icon={MousePointer}>
          <StyleSelect label="Cursor" value={elementStyle.cursor} onChange={handleDirectChange('cursor')} options={CURSOR_OPTIONS} />
        </CollapsibleSection>

      </div>
    </div>
  );
};

export default PropertiesPanel;
